/**
 * Action wrapper for archiving/unarchiving a patient.
 *
 * Exports both a controlled dialog (`ArchivePatientDialog`) for use inside
 * dropdown menus and a self-contained action (`ArchivePatientAction`) with
 * a render prop trigger.
 */
import { useState } from 'react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/frontend/components/ui/alert-dialog'
import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { Label } from '@/frontend/components/ui/label'
import { Textarea } from '@/frontend/components/ui/textarea'
import { useArchivePatient } from '@/frontend/hooks/api/patients/use-archive-patient'
import { useUnarchivePatient } from '@/frontend/hooks/api/patients/use-unarchive-patient'

interface ArchivePatientDialogProps {
  patientId: string
  archivedAt: string | null
  ownerArchivedAt: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Controlled archive/unarchive dialog — renders the appropriate variant based on `archivedAt`. */
export function ArchivePatientDialog({
  patientId,
  archivedAt,
  ownerArchivedAt,
  open,
  onOpenChange,
}: ArchivePatientDialogProps) {
  if (archivedAt) {
    return (
      <UnarchiveDialog
        patientId={patientId}
        ownerArchivedAt={ownerArchivedAt}
        open={open}
        onOpenChange={onOpenChange}
      />
    )
  }
  return <ArchiveDialog patientId={patientId} open={open} onOpenChange={onOpenChange} />
}

interface ArchivePatientActionProps {
  patientId: string
  archivedAt: string | null
  ownerArchivedAt: string | null
  render: (props: { onClick: () => void }) => React.ReactNode
}

/** Self-contained action with render prop trigger and built-in dialog. */
export function ArchivePatientAction({
  patientId,
  archivedAt,
  ownerArchivedAt,
  render,
}: ArchivePatientActionProps) {
  const [open, setOpen] = useState(false)

  return (
    <>
      {render({ onClick: () => setOpen(true) })}
      <ArchivePatientDialog
        patientId={patientId}
        archivedAt={archivedAt}
        ownerArchivedAt={ownerArchivedAt}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  )
}

function ArchiveDialog({
  patientId,
  open,
  onOpenChange,
}: {
  patientId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [reason, setReason] = useState('')
  const archive = useArchivePatient(patientId)

  const trimmed = reason.trim()
  const canSubmit = trimmed.length > 0 && !archive.isPending

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    archive.mutate(
      { reason: trimmed },
      {
        onSuccess: () => {
          toast.success('Patient archived')
          onOpenChange(false)
          setReason('')
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Failed to archive patient')
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Archive patient</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-muted-foreground">
            Archiving keeps the patient's history but marks them as inactive. Provide a reason so
            the team has context.
          </p>
          <div className="space-y-2">
            <Label htmlFor="archive-reason">Reason</Label>
            <Textarea
              id="archive-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Pet has passed away, owner moved abroad..."
              maxLength={500}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={archive.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {archive.isPending ? 'Archiving…' : 'Archive'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function UnarchiveDialog({
  patientId,
  ownerArchivedAt,
  open,
  onOpenChange,
}: {
  patientId: string
  ownerArchivedAt: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const unarchive = useUnarchivePatient(patientId)

  function handleConfirm() {
    unarchive.mutate(undefined, {
      onSuccess: () => {
        toast.success('Patient restored')
        onOpenChange(false)
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to restore patient')
      },
    })
  }

  if (ownerArchivedAt) {
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot unarchive patient</AlertDialogTitle>
            <AlertDialogDescription>
              An admin must unarchive this patient's owner before the patient can be unarchived.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Unarchive patient?</AlertDialogTitle>
          <AlertDialogDescription>
            This will restore the patient and clear the archive reason.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={unarchive.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={unarchive.isPending}>
            {unarchive.isPending ? 'Restoring…' : 'Unarchive'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
